import { z } from "zod";

// Constants for validation constraints
const PROJECT_NAME_MIN_LENGTH = 3;
const PROJECT_NAME_MAX_LENGTH = 50;
const PROJECT_DESCRIPTION_MAX_LENGTH = 200;

export type Project = {
  project_id: string;
  tenant_id: string;
  name: string;
  description?: string | null;
  is_default: boolean;
  created_at: string;
  // Whether the current user can edit this project (set by the backend). When it
  // isn't true, hide the edit controls — the backend rejects the edit either way.
  can_edit?: boolean;
  // Whether the current user can invite members to this project (set by the
  // backend). When it isn't true, hide the invite control — the backend rejects
  // the invitation either way.
  can_invite?: boolean;
};

export type SingleProjectResponse = {
  code: number;
  msg: string;
  data: Project;
};

export const CreateProjectSchema = z.object({
  name: z
    .string()
    .min(PROJECT_NAME_MIN_LENGTH, "Name must be at least 3 characters")
    .max(PROJECT_NAME_MAX_LENGTH, "Name must be less than 50 characters")
    .regex(
      /^[a-zA-Z0-9-_ ]+$/,
      "Name can only contain letters, numbers, spaces, hyphens, and underscores"
    ),
  description: z
    .string()
    .max(
      PROJECT_DESCRIPTION_MAX_LENGTH,
      "Description must be less than 200 characters"
    )
    .optional(),
});

export type CreateProjectFormData = z.infer<typeof CreateProjectSchema>;

const UpdateProjectSchema = CreateProjectSchema.extend({
  project_id: z.string(),
});

type UpdateProjectFormData = z.infer<typeof UpdateProjectSchema>;

// For partial updates, project_id is always required
export type PartialUpdateProjectFormData = {
  project_id: string;
} & Partial<Omit<UpdateProjectFormData, "project_id">>;
